const { EntityApi } = require('../common')

const ForecastDataAdapter = require('./data-access/forecast-data-adapter')
const ForecastRestAdapter = require('./data-access/forecast-rest-adapter')
const { toProductLevels } = require('./tools/read/to-forecast-per-location')
const forecastToLevels = require('./tools/read/forecast-to-levels')
const allocConfigToForecast = require('./tools/read/alloc-config-to-forecast')
const psmReportToForecast = require('./tools/read/psm-report-to-forecast')

class ForecastApi extends EntityApi {
  constructor (state, pgConnection, agaveAdapter) {
    let adapter = {}
    if (pgConnection) {
      adapter = new ForecastDataAdapter(pgConnection)
    } else if (agaveAdapter) {
      adapter = new ForecastRestAdapter({ agaveAdapter })
    }
    super(adapter)
  }

  get (...params) {
    return this.adapter.get(...params)
  }

  getAggregated (...params) {
    return this.adapter.getAggregated(...params)
  }

  // TODO: right now the only allowed params are
  // `effectiveOnDate` and `locationIds`.
  // In the future it should be possible to request all
  // forecasts that were active in a date range.
  //
  // One way of implementing this could be to allow passing
  // to `effectiveOnDate` an ISO string or an object like
  // ```
  // {
  //   from: <ISOString>,
  //   to: <ISOString>
  // }
  // ```
  async list (params = {}, { forecastSource = 'smartForecast' } = {}) {
    if (forecastSource === 'smartForecast') {
      return this.adapter.list(params)
    }

    const {
      effectiveOnDate,
      locationIds
    } = params

    // PSM zones: base forecasts in remote allocation configs
    if (forecastSource === 'allocation') {
      const allocConfigs = await this.mainApi.allocation.getAllConfigurations({
        date: effectiveOnDate,
        includeDemandPlan: true,
        [locationIds && 'facilityIds']: locationIds
      })

      return allocConfigs.map(allocConfig => allocConfigToForecast(allocConfig)).filter(Boolean)
    }

    // PSM state and SDPs: base forecasts in reported consumption and fixed
    // supply plans
    if (forecastSource === 'report') {
      const { reports = {} } = params
      const forecasts = []
      for (const locationId of locationIds) {
        const report = reports[locationId]
        if (!report) {
          continue
        }
        const periodDefinition = await this.mainApi.report.period.getDefinition({
          programId: report.serviceId.split(':service')[0]
        })
        forecasts.push(psmReportToForecast(locationId, report, periodDefinition))
      }
      return forecasts.filter(exist => exist)
    }

    throw new Error('Error: unknown "forecastSource", supported sources are "smartForecast", "allocation" and "report"')
  }

  async getLevels ({ locationIds, program, effectiveOnDate = new Date().toISOString(), reports = {} } = {}, { forecastSource = 'smartForecast' } = {}) {
    const forecasts = await this.list({ effectiveOnDate, locationIds, program, reports }, { forecastSource })

    if (forecastSource === 'smartForecast') {
      return forecasts.reduce((index, forecast) => {
        index[forecast.locationId] = index[forecast.locationId] || {}
        index[forecast.locationId][forecast.program] = { products: toProductLevels(forecast) }
        return index
      }, {})
    }

    // In PSM (`forecastSource` report or allocation) the min/max thresholds are set in months, not days
    const supplyTimeUnit = forecastSource === 'allocation' || forecastSource === 'report' ? 'month' : 'day'
    return forecasts.reduce((index, forecast) =>
      Object.assign(index, { [forecast.locationId]: forecastToLevels(forecast, supplyTimeUnit) })
      , {})
  }
}

module.exports = ForecastApi
